import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { Button, Page, PageContentHeader, Spinner, TableList } from 'seagull';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { selectors } from '~/store/state-selectors';
import { formatLongMonthUSDate } from '~/utils';
import TableFilters from '../components/TableFilters/TableFilters';
import styles from './RemainingCredit.module.scss';
import { makeCsv, headerItems, filterLabels, getDefaultSort, makeFiltersConfig, remainingCreditReportColumnDefs, remainingCreditReportHeaderColumnDefsForExport } from './RemainingCredit.utils';
import { RemainingCreditRow } from './RemainingCreditRow';
import { FilterType } from './types';
const testIdPrefix = 'remaining-credit-report';
const title = 'Remaining Credit Report';
export const RemainingCreditReport = ()=>{
    const history = useHistory();
    const [remainingCreditReportData, setRemainingCreditReportData] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const { downloadCsv } = useIoContext();
    const sortIds = compact(headerItems.map((item)=>item.sortId));
    const defaultSort = useMemo(()=>getDefaultSort(sortIds), [
        sortIds
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const { api } = useStoreActions();
    const businessName = useStore(selectors.user.businessName);
    const handleFetchFilterConfig = async ()=>{
        const [serviceNames, customerNames, agreementNames] = await Promise.all([
            api.reports.remainingCreditReportServices(),
            api.reports.remainingCreditReportCustomers(),
            api.reports.remainingCreditReportAgreements()
        ]);
        const filterOptions = {
            [FilterType.CustomerName]: customerNames,
            [FilterType.ServiceName]: serviceNames,
            [FilterType.AgreementName]: agreementNames
        };
        return makeFiltersConfig(filterOptions);
    };
    const fetchRemainingCreditReport = (selectedFilters, sort)=>{
        setIsLoading(true);
        api.reports.remainingCreditReport({
            customers: selectedFilters[FilterType.CustomerName] || [],
            services: selectedFilters[FilterType.ServiceName] || [],
            agreements: selectedFilters[FilterType.AgreementName] || [],
            sort: sort ? [
                sortIds[sort.colIndex],
                sort.sort
            ] : undefined
        }).then(setRemainingCreditReportData).finally(()=>setIsLoading(false));
    };
    useSetDocumentTitle(title);
    useOnMount(()=>{
        fetchRemainingCreditReport(selectedFilters, sort);
    });
    const onExport = ()=>downloadCsv(makeCsv(remainingCreditReportHeaderColumnDefsForExport.map((param)=>{
            let { value } = param;
            return value;
        }), remainingCreditReportData), "".concat(businessName, "_remaining_credit_report_").concat(formatLongMonthUSDate(new Date()), ".csv"));
    const onSortChange = (sort)=>{
        changeSort(sort);
        fetchRemainingCreditReport(selectedFilters, sort);
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: testIdPrefix,
        header: title,
        variant: "containedHeight",
        onClickBack: ()=>history.push(routes.reports),
        actions: /*#__PURE__*/ _jsx(Button, {
            skin: "primary",
            testId: "".concat(testIdPrefix, "-export-btn"),
            onClick: onExport,
            children: "Export"
        }),
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            columnDefs: remainingCreditReportColumnDefs,
            numRows: remainingCreditReportData.length,
            sort: sort,
            onSortChange: onSortChange,
            children: [
                /*#__PURE__*/ _jsx(PageContentHeader, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.pageContent,
                        children: /*#__PURE__*/ _jsx(RestfulWrapper, {
                            fetch: handleFetchFilterConfig,
                            renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                                    size: 24
                                }),
                            renderChild: (param)=>{
                                let { data: filterConfig } = param;
                                return /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: testIdPrefix,
                                    filterLabels: filterLabels,
                                    filterConfig: filterConfig,
                                    selectedFilters: selectedFilters,
                                    setSelectedFilters: setSelectedFilters,
                                    onRefresh: fetchRemainingCreditReport
                                });
                            }
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(TableList.Structure, {
                    header: /*#__PURE__*/ _jsx(TableList.Header, {
                        stickyToTop: true,
                        items: headerItems,
                        testId: "".concat(testIdPrefix, "-table-header")
                    }),
                    content: isLoading ? /*#__PURE__*/ _jsx(Spinner, {
                        centeredInBox: true
                    }) : /*#__PURE__*/ _jsx(_Fragment, {
                        children: remainingCreditReportData.map((reportRow, index)=>/*#__PURE__*/ _jsx(RemainingCreditRow, {
                                index: index,
                                data: reportRow
                            }, "".concat(reportRow.customerName, "-").concat(reportRow.serviceName, "-").concat(index)))
                    })
                })
            ]
        })
    });
};
